// App.js
import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ActivityIndicator, Platform } from 'react-native';
import { NavigationContainer, createNavigationContainerRef, getStateFromPath } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { ErrorProvider } from './src/components/ErrorContext';
import { CookieConsentProvider } from './src/context/CookieConsentContext';
import { MatchReadyProvider } from './src/context/MatchReadyContext';
import { CartProvider } from './src/context/CartContext';
import { auth } from './firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import GameStoreScreen from './src/screens/GameStoreScreen'; // Kept for post-launch restoration when merch is fulfilled
import FounderStoreScreen from './src/screens/FounderStoreScreen';
import MerchOrderConfirmationScreen from './src/screens/MerchOrderConfirmationScreen';
import MerchOrderHistoryScreen from './src/screens/MerchOrderHistoryScreen';
import MerchCheckoutScreen from './src/screens/MerchCheckoutScreen';
import CartScreen from './src/screens/CartScreen';
import GameLibraryScreen from './src/screens/GameLibraryScreen';
import GameDetailScreen from './src/screens/GameDetailScreen';
import DesktopDownloadScreen from './src/screens/DesktopDownloadScreen';
import AdvertiserDashboardScreen from './src/screens/AdvertiserDashboardScreen';
import InviteCodeScreen from './src/screens/InviteCodeScreen';
import KingCritChat from './src/components/KingCritChat';
import TosUpdateModal from './src/components/TosUpdateModal';
import FinishOnboardingModal from './src/components/FinishOnboardingModal';
import { CURRENT_TOS_VERSION, isTosVersionBehind } from './src/constants/tosUpdate';
import HostRoomIdModal from './src/components/HostRoomIdModal';
import useDesktopAuthBridge, { nextGateKnownUid, isTrustedUsersSnapshot, subscribeDesktopNavigate } from './src/hooks/useDesktopAuthBridge';
import { isDesktopApp } from './src/utils/platform';

const Stack = createStackNavigator();

// Used to read the active route name so global overlays (e.g. King Crit) can be
// hidden on specific screens. King Crit is hidden on EventWatch and StreamWatch —
// it overlaps the video/controls and the chat input there.
const navigationRef = createNavigationContainerRef();
// DevGallery: keep the concierge bubble out of marketing screenshots.
const HIDE_CONCIERGE_ROUTES = ['EventWatch', 'StreamWatch', 'TeamStream', 'DevGallery', 'StreamChatPopout'];

// Routes where the Terms-update prompt must NOT render. It is a full-screen
// overlay, so showing it on the Terms itself covers the very text the member
// was sent to read — including in a second tab, which renders this same shell.
// Everywhere else it keeps showing until they accept.
// Memberships, Subscription and CreatorMemberships are exempt too (evergreen
// 3.5a): no cancel, exit or moderation control may sit behind a prompt that
// cannot be dismissed. Each gates its own purchases (the membership checkout
// is also refused by the server on stale Terms; the creator switch-on is not
// Terms-gated while memberships.js REQUIRE_CREATOR_TERMS_ON_ENABLE is false,
// founder call 18.9, so the screen's Terms row is a nudge).
// ConfirmEmail (R6): the confirm-email page shows its one result (J12 /
// J12x) unobstructed; the prompt returns on the next route. ForgotPassword
// and ResetPassword (forgot password): the reset page signs the person in,
// and its result (the new password is saved) must stay readable.
// CreatorTeamMemberships joins them (evergreen 3.5b): ending, removing and
// switching squad chat off never wait on the prompt. The captain's switch-on
// is not Terms-gated either while REQUIRE_CREATOR_TERMS_ON_ENABLE is false
// (the same flag as the channel's); the screen's Terms row is only a nudge.
// TeamPage is NOT exempt (a public page; its Join is server-gated).
const HIDE_TOS_PROMPT_ROUTES = [
  'TosAgreement', 'Legal', 'SignUp', 'Login', 'Memberships', 'Subscription', 'CreatorMemberships', 'ConfirmEmail',
  'ForgotPassword', 'ResetPassword', 'CreatorTeamMemberships',
];

// Routes where the finish-onboarding guard must NOT render: the screens that
// ARE the completion flow (SignUp wizard, Login + the Discord/Twitch callback
// leg that lands there), the entry splash, and the legal pages the wizard
// links out to. Everywhere else a signed-in account with no users doc or a
// pending one (onboardingComplete:false) is asked to finish. The forgot
// password pages too: the reset page's result stays readable after it signs
// the person in ("Back to CritCast" goes to Home, where the guard shows).
const HIDE_ONBOARDING_PROMPT_ROUTES = [
  'SignUp', 'Login', 'AuthCallback', 'Landing', 'TosAgreement', 'Legal', 'RestoreAccount',
  'ForgotPassword', 'ResetPassword',
];

// Browser-tab / share-sheet titles per route (documentTitle formatter below).
// Anything not listed shows plain "CritCash". Data-driven screens override
// after load (setPageMeta) — e.g. StreamWatch becomes "<name> is LIVE …".
const DOC_TITLES = {
  // The splash leads with CritCast (founder 2026-10-12); mirrors the meta
  // title in scripts/inject-og.js and src/utils/pageMeta.js.
  Landing: 'CritCast | The streaming platform with the tools built in',
  Home: 'Home — CritCash',
  Lobby: 'Events — CritCash',
  LiveMatches: 'Live Matches — CritCash',
  Streams: 'Streams — CritCash',
  Leaderboard: 'Leaderboard — CritCash',
  Store: 'Store — CritCash',
  MyLeagues: 'Leagues — CritCash',
  HowToPlay: 'How to Play — CritCash',
};

// =============================================================================
// DEEP LINKING CONFIGURATION
// =============================================================================
// This maps URLs to screens, enabling:
// 1. Shareable links (Discord, social media, etc.)
// 2. Page reload stays on current screen
// 3. Browser back/forward buttons work
// =============================================================================
const linking = {
  prefixes: [
    'https://critcash.com',
    'https://www.critcash.com',
    'critcash://',
  ],
  config: {
    screens: {
      // Public pages (no auth required)
      Landing: '',
      Login: 'login',
      // OAuth redirect landing (Discord/Twitch send the user to
      // /auth/<provider>/callback?code=...). Routes to the LoginScreen
      // component, which runs the code-exchange handler and then forwards
      // new/pending users into the signup completion wizard. Without this
      // the path was unmatched and fell back to the splash screen, so the
      // callback never ran (redirect loop).
      AuthCallback: 'auth/:provider/callback',
      InviteCode: 'invite',
      SignUp: 'signup',
      StreamOverlay: 'overlay',
      Legal: 'legal',
      About: 'about',
      HowToPlay: 'how-to-play',
      FAQ: 'faq',
      HelpSupport: 'support',
      ResponsibleGaming: 'responsible-gaming',
      RestoreAccount: 'restore-account',
      // The link in our confirmation email (R6, 3.13.3):
      // /confirm-email?code=<oobCode>&to=<join|verification>[&ch=<channel>];
      // the query params arrive as route params. No sign-in needed.
      ConfirmEmail: 'confirm-email',
      // Forgot password: the Forgot page, and the link in our reset email
      // (/reset-password?code=<oobCode>; the code arrives as a route param
      // and the page drops it as soon as the page has loaded). No sign-in
      // needed.
      ForgotPassword: 'forgot-password',
      ResetPassword: 'reset-password',
      Jobs: 'jobs',
      Store: 'store',
      Cart: 'cart',
      MerchCheckout: 'merch-checkout',
      MerchOrderConfirmation: 'merch-order-confirmation',
      MerchOrderHistory: 'orders',
      GameLibrary: 'library',
      Download: 'download',
      
      // Partner/Business pages
      DeveloperPortal: 'developers',
      DeveloperDashboard: 'developer-dashboard',
      Advertiser: 'advertise',
      AdvertiserDashboard: 'advertiser-dashboard',
      
      // Game Detail (supports /store/critcash_arena)
      GameDetail: {
        path: 'store/:gameId',
        parse: {
          gameId: (gameId) => gameId,
        },
      },
      
      // Main app screens
      Home: 'home',
      Lobby: 'lobby', // the events list; public URL kept (evergreen Step 1.2)
      LiveMatches: 'live',
      Streams: 'streams',
      // Personal stream watch page (deep-linkable so a reload keeps the
      // stream). :channel is the PUBLIC userName (uid in the URL was a leak —
      // 2026-07-23); legacy /s/<uid> links still resolve server-side.
      StreamWatch: { path: 's/:channel', parse: { channel: (channel) => channel } },
      // Chat-only popout (no player) — OBS Custom Browser Dock / teleprompter
      StreamChatPopout: { path: 'chat/:channel', parse: { channel: (channel) => channel } },
      // Squad (team stream) watch page — Team Streams v1
      TeamStream: { path: 'ts/:teamStreamId', parse: { teamStreamId: (teamStreamId) => teamStreamId } },
      // Public team page (evergreen 3.5b): the evergreen home of a team and
      // its team membership. /t/ is short and does not collide with /team.
      TeamPage: { path: 't/:teamId', parse: { teamId: (teamId) => teamId } },
      Leaderboard: 'leaderboard',
      PublicProfile: { path: 'u/:userName', parse: { userName: (userName) => userName } },
      VideoWatch: { path: 'v/:videoId', parse: { videoId: (videoId) => videoId } },
      MyVideos: 'my-videos',
      ProfileBadge: 'profile-badge',
      
      // Event screens (with parameters)
      // Supports both:
      //   /event/abc123?collection=tournaments
      //   /event?id=abc123&collection=tournaments (legacy)
      EventDetailScreen: {
        path: 'event/:eventId?',
        parse: {
          eventId: (eventId) => eventId === 'undefined' ? null : eventId,
        },
        // Query params (?id=...&collection=...) are automatically merged into params
      },
      Championship: {
        path: 'championship/:slug',
        parse: {
          slug: (slug) => slug,
        },
      },
      BracketHub: {
        path: 'bracket/:eventId',
        parse: {
          eventId: (eventId) => eventId,
        },
      },
      // Per-match lobby — deep-linkable so a reload mid-heat restores the
      // lobby instead of dropping to the home screen.
      MatchLobby: {
        path: 'match/:eventId/:matchId',
        parse: {
          eventId: (eventId) => eventId,
          matchId: (matchId) => matchId,
        },
      },
      // Live spectator view. Needs its own deep-linkable path so a browser
      // reload while watching restores EventWatch (with the eventId) instead
      // of dropping the viewer back to the lobby. Optional ?collection= query
      // param is auto-merged into params.
      EventWatch: {
        path: 'watch/:eventId',
        parse: {
          eventId: (eventId) => eventId,
        },
      },

      // User screens
      Profile: 'profile',
      Verification: 'verification',
      IdVerification: 'id-verification',
      Wallet: 'wallet',
      CreatorMetrics: 'creator-metrics',
      Subscription: 'subscription',
      Memberships: 'memberships',
      CreatorMemberships: 'creator/memberships',
      CreatorTeamMemberships: 'creator/team-memberships',
      GiftCard: 'gift-cards',
      StreamingAccounts: 'streaming',
      DeleteAccount: 'delete-account',
      AccountPendingDeletion: 'account-pending',
      ChangePassword: 'change-password',
      PhoneVerificationScreen: 'phone-verification',
      TosAgreement: 'tos',
      YourTeam: 'team',
      HostScreen: 'host',
      
      // Event creation
      CreateTournament: 'create',
      CreateOfficialTournament: 'create-official',

      // Leagues (round-robin + full-grid race series)
      CreateLeague: 'create-league',
      LeagueDetail: {
        path: 'league/:leagueId',
        parse: {
          leagueId: (leagueId) => leagueId,
        },
      },
      LeagueCommandCenter: {
        path: 'league-manage/:leagueId',
        parse: {
          leagueId: (leagueId) => leagueId,
        },
      },
      
      // Admin screens (protected by auth in component)
      AdminDashboard: 'admin',
      AdminTools: 'admin/tools',
      ManageEvents: 'admin/events',
      ManageUsers: 'admin/users',
      Financials: 'admin/financials',
      ActiveMatches: 'admin/active-matches',
      PendingMatches: 'admin/pending-matches',
      PaymentSettings: 'admin/payments',
      GameAvailability: 'admin/game-availability',
      AdminReconciliation: 'admin/reconciliation',
      AdminExpenses: 'admin/expenses',
      AdminTaxCompliance: 'admin/tax-compliance',
      AdminCompOverview: 'admin/comp-overview',
      AdminGameDemand: 'admin/game-demand',
      AdminDisputes: 'admin/disputes',
      AdminBrandedEvents: 'admin/branded-events',
      TaxInfo: 'tax-info',
      AdminUserDetailScreen: {
        path: 'admin/user/:userId',
        parse: {
          userId: (userId) => userId,
        },
      },
      // Key must match the Stack.Screen name ('EventEvidence') or the URL
      // never resolves — founder result-conflict emails deep-link here.
      EventEvidence: {
        path: 'admin/evidence/:eventId',
        parse: {
          eventId: (eventId) => eventId,
        },
      },

      // Dev-only screenshot gallery (self-gates to localhost/dev builds)
      DevGallery: 'dev/gallery',
    },
  },
};

// =============================================================================
// SCREEN IMPORTS
// =============================================================================
import LandingScreen from './src/screens/LandingScreen';
import LoginScreen from './src/screens/LoginScreen';
import SignUpWizard from './src/screens/SignUpWizard';
import LegalScreen from './src/screens/LegalScreen';
import LobbyScreen from './src/screens/LobbyScreen';
import HomeScreen from './src/screens/HomeScreen'; // evergreen Step 1.2: Home is the lobby
import AboutScreen from './src/screens/AboutScreen';
import ProfileSettingsScreen from './src/screens/ProfileSettingsScreen';
import SubscriptionScreen from './src/screens/SubscriptionScreen';
import MembershipsScreen from './src/screens/MembershipsScreen';
import CreatorMembershipsScreen from './src/screens/CreatorMembershipsScreen';
import CreatorTeamMembershipsScreen from './src/screens/CreatorTeamMembershipsScreen';
import { TeamPage } from './src/screens/TeamPageScreen';
import VerificationScreen from './src/screens/VerificationScreen';
import ConfirmEmailScreen from './src/screens/ConfirmEmailScreen';
import ForgotPasswordScreen from './src/screens/ForgotPasswordScreen';
import ResetPasswordScreen from './src/screens/ResetPasswordScreen';
import IdVerificationScreen from './src/screens/IdVerificationScreen';
import StreamingAccountsScreen from './src/screens/StreamingAccountsScreen';
import PhoneVerificationScreen from './src/screens/PhoneVerificationScreen';
import TosAgreementScreen from './src/screens/TosAgreementScreen';
import DeleteAccountScreen from './src/screens/DeleteAccountScreen';
import HowToPlayScreen from './src/screens/HowToPlay';
import FAQScreen from './src/screens/FAQScreen';
import HelpSupportScreen from './src/screens/HelpSupportScreen';
import ResponsibleGamingScreen from './src/screens/ResponsibleGamingScreen';
import AccountPendingDeletionScreen from './src/screens/AccountPendingDeletionScreen';
import ChangePasswordScreen from './src/screens/ChangePasswordScreen';
import RestoreAccountScreen from './src/screens/RestoreAccountScreen';
import YourTeam from './src/screens/YourTeam';
import ManageEventsScreen from './src/screens/ManageEventsScreen';
import ManageUsersScreen from './src/screens/ManageUsersScreen';
import FinancialsScreen from './src/screens/FinancialsScreen';
import BracketHubScreen from './src/screens/BracketHubScreen';
import HostProgramScreen from './src/screens/HostProgramScreen';
import EventEvidenceScreen from './src/screens/EventEvidenceScreen';
import CreateEventScreen from './src/screens/CreateEventsScreen';
import AdminDashboardScreen from './src/screens/AdminDashboardScreen';
import ActiveMatchesScreen from './src/screens/ActiveMatches';
import PendingMatchesScreen from './src/screens/PendingMatchesScreen';
import AdminToolsScreen from './src/screens/AdminToolsScreen';
import LiveMatchesScreen from './src/screens/LiveMatchesScreen';
import EventDetailScreen from './src/screens/EventDetailScreen';
import ChampionshipScreen from './src/screens/ChampionshipScreen';
import MatchLobbyScreen from './src/screens/MatchLobbyScreen';
import AdminUserDetailScreen from './src/screens/AdminUserDetailScreen';
import GameProfilesScreen from './src/screens/GameProfileScreen';
import PlatformAccountsScreen from './src/screens/PlatformAccountsScreen';
import NotificationsScreen from './src/screens/NotificationsScreen';
import PaymentSettingsScreen from './src/screens/PaymentSettingsScreen';
import WalletScreen from './src/screens/WalletScreen';
import GiftCardScreen from './src/screens/GiftCardScreen';
import EventWatchScreen from './src/screens/EventWatchScreen';
import StreamsScreen from './src/screens/StreamsScreen';
import StreamWatchScreen from './src/screens/StreamWatchScreen';
import StreamChatPopoutScreen from './src/screens/StreamChatPopoutScreen';
import TeamStreamScreen from './src/screens/TeamStreamScreen';
import CreatorMetricsScreen from './src/screens/CreatorMetricsScreen';
import LeaderboardScreen from './src/screens/LeaderboardScreen';
import CreateLeagueScreen from './src/screens/CreateLeagueScreen';
import LeagueDetailScreen from './src/screens/LeagueDetailScreen';
import LeagueCommandCenterScreen from './src/screens/LeagueCommandCenterScreen';
import MyLeaguesScreen from './src/screens/MyLeaguesScreen';
import PublicProfileScreen from './src/screens/PublicProfileScreen';
import VideoWatchScreen from './src/screens/VideoWatchScreen';
import MyVideosScreen from './src/screens/MyVideosScreen';
import ProfileBadgeScreen from './src/screens/ProfileBadgeScreen';
import GameAvailabilityScreen from './src/screens/GameAvailabilityScreen';
import JobsScreen from './src/screens/JobsScreen';

// Partner/Business screens
import StreamOverlayScreen from './src/screens/StreamOverlayScreen';
import DeveloperPortalScreen from './src/screens/DeveloperPortalScreen';
import DeveloperDashboardScreen from './src/screens/DeveloperDashboardScreen';
import AdminStreamerOutreachScreen from './src/screens/AdminStreamerOutreachScreen';
import AdminAmlReviewScreen from './src/screens/AdminAmlReviewScreen';
import AdminReconciliationScreen from './src/screens/AdminReconciliationScreen';
import AdminExpensesScreen from './src/screens/AdminExpensesScreen';
import AdminTaxComplianceScreen from './src/screens/AdminTaxComplianceScreen';
import AdminCompOverviewScreen from './src/screens/AdminCompOverviewScreen';
import AdminGameDemandScreen from './src/screens/AdminGameDemandScreen';
import AdminDisputesScreen from './src/screens/AdminDisputesScreen';
import AdminBrandedEventsScreen from './src/screens/AdminBrandedEventsScreen';
import TaxInfoScreen from './src/screens/TaxInfoScreen';
import AdvertiserScreen from './src/screens/AdvertiserScreen';
import DevGalleryScreen from './src/screens/DevGalleryScreen';

// =============================================================================
// APP COMPONENT
// =============================================================================
export default function App() {
  // --- Stream overlay: render completely outside all wrappers ---
  // OBS Browser Source needs zero providers (no cookies, no auth, no nav chrome).
  // Rendering outside NavigationContainer also guarantees a transparent background.
  if (Platform.OS === 'web' && window.location.pathname === '/overlay') {
    const params = Object.fromEntries(new URLSearchParams(window.location.search));
    return <StreamOverlayScreen route={{ params }} />;
  }

  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [currentRouteName, setCurrentRouteName] = useState(null);
  // Terms re-acceptance. A version bump used to bind NEW signups only — nothing
  // ever compared a member's stored version against the current one, so clauses
  // never reached the people already here. Re-checked on every users-doc snapshot.
  const [needsTosAccept, setNeedsTosAccept] = useState(false);
  // The stored verification.tos.version, for the cumulative "what's new" list
  // (a member two versions behind sees both lists). null when unknown.
  const [tosAcceptedVersion, setTosAcceptedVersion] = useState(null);
  // Session acknowledgement of the Terms prompt: set to CURRENT_TOS_VERSION when
  // the member accepts, or when a platform failure steps the prompt aside, so a
  // users-doc snapshot re-firing later in the session can't bring it back.
  // Cleared on sign-out / account switch (authUidRef = whose session it is).
  const tosAckRef = useRef(null);
  const authUidRef = useRef(null);
  const ackTosForSession = () => {
    tosAckRef.current = CURRENT_TOS_VERSION;
    setNeedsTosAccept(false);
  };
  // Finish-onboarding guard: signed in but users/{uid} is missing or pending.
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  // Desktop app (CritCast) only: the uid whose users-doc verdict is in, so the
  // bridge never hands the app a token before Terms and onboarding are known.
  // Stays null in a normal browser. The 10 s fail-open timeout (S2.2 item 2)
  // lives in useDesktopAuthBridge, not here: after it fires the hook treats the
  // uid as known while this state stays null, so only the hook reads it.
  const [gateKnownUid, setGateKnownUid] = useState(null);
  // Debounce the VISIBLE condition (pending state AND a route that shows the
  // guard), not needsOnboarding alone: needsOnboarding is true for the whole
  // wizard session, so a timer keyed on it alone would be long expired by the
  // time the wizard does replace('Lobby') — a beat before the doc listener
  // sees onboardingComplete:true — and the guard would flash on Lobby.
  // Restarting the timer on the route edge is what covers that beat.
  // Clearing is immediate; showing waits ~700ms of sustained visible state.
  const wantOnboardingGuard = needsOnboarding
    && !!currentRouteName
    && !HIDE_ONBOARDING_PROMPT_ROUTES.includes(currentRouteName);
  const [showOnboardingGuard, setShowOnboardingGuard] = useState(false);
  useEffect(() => {
    if (!wantOnboardingGuard) { setShowOnboardingGuard(false); return; }
    const t = setTimeout(() => setShowOnboardingGuard(true), 700);
    return () => clearTimeout(t);
  }, [wantOnboardingGuard]);

  // Wait for Firebase Auth to restore session before rendering navigation
  useEffect(() => {
    let unsubDoc = null;
    const markGate = (event) => {
      if (isDesktopApp()) setGateKnownUid((prev) => nextGateKnownUid(prev, event, authUidRef.current));
    };
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      if (unsubDoc) { unsubDoc(); unsubDoc = null; }
      const uid = currentUser?.uid || null;
      if (uid !== authUidRef.current) { authUidRef.current = uid; tosAckRef.current = null; }
      markGate({ type: 'user', uid });
      if (!currentUser) { setNeedsTosAccept(false); setTosAcceptedVersion(null); setNeedsOnboarding(false); return; }
      // Live users-doc listener (was a one-shot read). Drives two guards:
      //   - Terms re-acceptance (stored tos version behind CURRENT_TOS_VERSION)
      //   - Finish-onboarding (no doc, or onboardingComplete:false — every OAuth
      //     signup is pending server-side until completeOAuthSignup finalizes;
      //     the listener clears the guard the instant that write lands).
      // Read-only; a failure must never gate the app behind a modal the member
      // cannot dismiss, so any error leaves BOTH prompts hidden.
      (async () => {
        try {
          const { getFirestore, doc, onSnapshot } = await import('firebase/firestore');
          // Auth moved on while the import resolved: no listener for an old account.
          if (authUidRef.current !== currentUser.uid) return;
          unsubDoc = onSnapshot(
            doc(getFirestore(), 'users', currentUser.uid),
            (snap) => {
              const data = snap.exists() ? (snap.data() || {}) : null;
              const accepted = data ? data?.verification?.tos?.version : null;
              setTosAcceptedVersion(accepted || null);
              // Prompt only when the stored version is numerically OLDER than
              // this bundle's (a stale bundle never re-prompts a member who is
              // ahead), never a pending account (the finish-onboarding guard
              // owns that, and acceptTos refuses it), and not again this
              // session once acknowledged.
              setNeedsTosAccept(!!accepted
                && data.onboardingComplete !== false
                && isTosVersionBehind(accepted)
                && tosAckRef.current !== CURRENT_TOS_VERSION);
              // fromCache + missing can be a cold local cache before the server
              // answers — only trust "missing" from the server.
              const missing = !snap.exists() && !snap.metadata?.fromCache;
              setNeedsOnboarding(missing || (!!data && data.onboardingComplete === false));
              markGate({ type: 'snapshot', uid: currentUser.uid, trusted: isTrustedUsersSnapshot(snap) });
            },
            () => { setNeedsTosAccept(false); setNeedsOnboarding(false); markGate({ type: 'error', uid: currentUser.uid }); }
          );
        } catch (e) {
          setNeedsTosAccept(false);
          setNeedsOnboarding(false);
          markGate({ type: 'error', uid: currentUser.uid });
        }
      })();
    });
    return () => { if (unsubDoc) unsubDoc(); unsubscribe(); };
  }, []);

  // Desktop app (CritCast): hand this session to the app and answer its token
  // and sign-out requests. No-op outside the app.
  useDesktopAuthBridge({ user, gateKnownUid, needsOnboarding, needsTosAccept, navigationRef });
  // Desktop app: the Broadcast panel and tray open site pages in this view;
  // the ack tells the app to skip its full-page fallback. No-op outside the app.
  useEffect(() => subscribeDesktopNavigate({ navigationRef, config: linking.config, getStateFromPath }), []);

  // Show loading while checking auth state
  // This prevents the app from redirecting to login before auth is ready
  if (authLoading) {
    return <AuthLoadingScreen />;
  }

  return (
    <ErrorProvider>
      <CookieConsentProvider>
      <MatchReadyProvider>
          <CartProvider>
            <NavigationContainer
              ref={navigationRef}
              linking={linking}
              fallback={<LoadingScreen />}
              onReady={() => setCurrentRouteName(navigationRef.getCurrentRoute()?.name)}
              onStateChange={() => setCurrentRouteName(navigationRef.getCurrentRoute()?.name)}
              // Without a formatter react-navigation stamps the raw route name
              // ("StreamWatch") into document.title — that's what sender-side
              // share sheets showed as the link title (founder 2026-07-23).
              // Screens with richer data (StreamWatch, PublicProfile) override
              // after load via utils/pageMeta setPageMeta().
              documentTitle={{
                formatter: (options, route) =>
                  options?.title ?? DOC_TITLES[route?.name] ?? 'CritCash',
              }}
            >
              <Stack.Navigator initialRouteName="Landing" screenOptions={{ headerShown: false }}>
                {/* Public screens */}
                <Stack.Screen name="Landing" component={LandingScreen} />
                <Stack.Screen name="Login" component={LoginScreen} />
                {/* Same component as Login — handles the /auth/:provider/callback
                    OAuth redirect so the code-exchange handler actually mounts. */}
                <Stack.Screen name="AuthCallback" component={LoginScreen} />
                <Stack.Screen name="InviteCode" component={InviteCodeScreen} />
                <Stack.Screen name="SignUp" component={SignUpWizard} />
                <Stack.Screen name="Legal" component={LegalScreen} />
                <Stack.Screen name="About" component={AboutScreen} />
                <Stack.Screen name="HowToPlay" component={HowToPlayScreen} />
                <Stack.Screen name="FAQ" component={FAQScreen} />
                <Stack.Screen name="HelpSupport" component={HelpSupportScreen} />
                <Stack.Screen name="Jobs" component={JobsScreen} />
                <Stack.Screen name="StreamOverlay" component={StreamOverlayScreen} />
                <Stack.Screen name="ResponsibleGaming" component={ResponsibleGamingScreen} />
                <Stack.Screen name="RestoreAccount" component={RestoreAccountScreen} />
                {/* Our email-confirmation link lands here (R6, J12 / J12x). */}
                <Stack.Screen name="ConfirmEmail" component={ConfirmEmailScreen} />
                {/* Forgot password: the Forgot page and our reset link's page. */}
                <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
                <Stack.Screen name="ResetPassword" component={ResetPasswordScreen} />
                
                {/* Partner/Business screens */}
                <Stack.Screen name="DeveloperPortal" component={DeveloperPortalScreen} />
                <Stack.Screen name="DeveloperDashboard" component={DeveloperDashboardScreen} />
                <Stack.Screen name="Advertiser" component={AdvertiserScreen} />
                <Stack.Screen name="AdvertiserDashboard" component={AdvertiserDashboardScreen} />
                
                {/* Main app screens */}
                <Stack.Screen name="Home" component={HomeScreen} />
                <Stack.Screen name="Lobby" component={LobbyScreen} />
                <Stack.Screen name="LiveMatches" component={LiveMatchesScreen} />
                <Stack.Screen name="Leaderboard" component={LeaderboardScreen} />
                <Stack.Screen name="PublicProfile" component={PublicProfileScreen} />
                <Stack.Screen name="VideoWatch" component={VideoWatchScreen} />
                <Stack.Screen name="MyVideos" component={MyVideosScreen} />
                <Stack.Screen name="ProfileBadge" component={ProfileBadgeScreen} />
                <Stack.Screen name="EventDetailScreen" component={EventDetailScreen} />
                <Stack.Screen name="Championship" component={ChampionshipScreen} />
                <Stack.Screen name="BracketHub" component={BracketHubScreen} />
                {/* Per-match lobby (GT7 heats/brackets) — notification taps and
                    the HEATS card's OPEN LOBBY navigate here */}
                <Stack.Screen name="MatchLobby" component={MatchLobbyScreen} />
                
                {/* User screens */}
                <Stack.Screen name="Profile" component={ProfileSettingsScreen} />
                <Stack.Screen name="Verification" component={VerificationScreen} />
                <Stack.Screen name="IdVerification" component={IdVerificationScreen} />
                <Stack.Screen name="Wallet" component={WalletScreen} />
                <Stack.Screen name="Subscription" component={SubscriptionScreen} />
                {/* Channel memberships (evergreen 3.5a): the viewer's own
                    memberships, and the creator's settings. Always registered,
                    so every link in a membership email or notice works. */}
                <Stack.Screen name="Memberships" component={MembershipsScreen} />
                <Stack.Screen name="CreatorMemberships" component={CreatorMembershipsScreen} />
                {/* Team memberships (evergreen 3.5b): the captain's settings, their
                    own screen (one price, no tiers; split from the channel
                    screen at the merge of live 3.5a). */}
                <Stack.Screen name="CreatorTeamMemberships" component={CreatorTeamMembershipsScreen} />
                <Stack.Screen name="GiftCard" component={GiftCardScreen} />
                <Stack.Screen name="StreamingAccounts" component={StreamingAccountsScreen} />
                <Stack.Screen name="DeleteAccount" component={DeleteAccountScreen} />
                <Stack.Screen name="PhoneVerificationScreen" component={PhoneVerificationScreen} />
                <Stack.Screen name="TosAgreement" component={TosAgreementScreen} />
                <Stack.Screen name="AccountPendingDeletion" component={AccountPendingDeletionScreen} />
                <Stack.Screen name="ChangePassword" component={ChangePasswordScreen} />
                <Stack.Screen name="YourTeam" component={YourTeam} />
                <Stack.Screen name="HostScreen" component={HostProgramScreen} />
                <Stack.Screen name="GameProfiles" component={GameProfilesScreen} />
                <Stack.Screen name="PlatformAccounts" component={PlatformAccountsScreen} />
                <Stack.Screen name="Notifications" component={NotificationsScreen} />
                <Stack.Screen name="EventWatch" component={EventWatchScreen} />
                <Stack.Screen name="Streams" component={StreamsScreen} />
                <Stack.Screen name="StreamWatch" component={StreamWatchScreen} />
                <Stack.Screen name="StreamChatPopout" component={StreamChatPopoutScreen} />
                <Stack.Screen name="TeamStream" component={TeamStreamScreen} />
                {/* The public team page (evergreen 3.5b); always registered, so
                    every link in a team notice or email works. */}
                <Stack.Screen name="TeamPage" component={TeamPage} />
                <Stack.Screen name="CreatorMetrics" component={CreatorMetricsScreen} />

                {/* Event creation */}
                <Stack.Screen name="CreateTournament" component={CreateEventScreen} />
                <Stack.Screen name="CreateOfficialTournament" component={CreateEventScreen} />

                {/* Leagues */}
                <Stack.Screen name="CreateLeague" component={CreateLeagueScreen} />
                <Stack.Screen name="LeagueDetail" component={LeagueDetailScreen} />
                <Stack.Screen name="LeagueCommandCenter" component={LeagueCommandCenterScreen} />
                <Stack.Screen name="MyLeagues" component={MyLeaguesScreen} />
                
                {/* Admin screens */}
                <Stack.Screen name="AdminDashboard" component={AdminDashboardScreen} />
                <Stack.Screen name="AdminTools" component={AdminToolsScreen} />
                <Stack.Screen name="ManageEvents" component={ManageEventsScreen} />
                <Stack.Screen name="ManageUsers" component={ManageUsersScreen} />
                <Stack.Screen name="Financials" component={FinancialsScreen} />
                <Stack.Screen name="ActiveMatches" component={ActiveMatchesScreen} />
                <Stack.Screen name="PendingMatches" component={PendingMatchesScreen} />
                <Stack.Screen name="AdminUserDetailScreen" component={AdminUserDetailScreen} />
                <Stack.Screen name="EventEvidence" component={EventEvidenceScreen} />
                <Stack.Screen name="PaymentSettings" component={PaymentSettingsScreen} />
                <Stack.Screen name="GameAvailability" component={GameAvailabilityScreen} />
                <Stack.Screen name="AdminStreamerOutreach" component={AdminStreamerOutreachScreen} />
                <Stack.Screen name="AdminAmlReview" component={AdminAmlReviewScreen} />
                <Stack.Screen name="AdminReconciliation" component={AdminReconciliationScreen} />
                <Stack.Screen name="AdminExpenses" component={AdminExpensesScreen} />
                <Stack.Screen name="AdminTaxCompliance" component={AdminTaxComplianceScreen} />
                <Stack.Screen name="AdminCompOverview" component={AdminCompOverviewScreen} />
                <Stack.Screen name="AdminGameDemand" component={AdminGameDemandScreen} />
                <Stack.Screen name="AdminDisputes" component={AdminDisputesScreen} />
                <Stack.Screen name="AdminBrandedEvents" component={AdminBrandedEventsScreen} />
                <Stack.Screen name="TaxInfo" component={TaxInfoScreen} />

                {/* Game Store & Library */}
                {/* Store route points to FounderStoreScreen (launch-phase). GameStoreScreen restored when merch fulfillment ships ~90 days post-launch. */}
                <Stack.Screen name="Store" component={FounderStoreScreen} />
                <Stack.Screen name="Cart" component={CartScreen} />
                <Stack.Screen name="MerchCheckout" component={MerchCheckoutScreen} />
                <Stack.Screen name="MerchOrderConfirmation" component={MerchOrderConfirmationScreen} />
                <Stack.Screen name="MerchOrderHistory" component={MerchOrderHistoryScreen} />
                <Stack.Screen name="GameDetail" component={GameDetailScreen} />
                <Stack.Screen name="GameLibrary" component={GameLibraryScreen} />
                <Stack.Screen name="Download" component={DesktopDownloadScreen} />

                {/* Dev-only screenshot gallery (self-gates to localhost) */}
                <Stack.Screen name="DevGallery" component={DevGalleryScreen} />
            </Stack.Navigator>
          </NavigationContainer>
          </CartProvider>
    </MatchReadyProvider>
      </CookieConsentProvider>
      {!HIDE_CONCIERGE_ROUTES.includes(currentRouteName) && <KingCritChat />}
      <HostRoomIdModal />
      <TosUpdateModal
        visible={needsTosAccept && !HIDE_TOS_PROMPT_ROUTES.includes(currentRouteName)}
        acceptedVersion={tosAcceptedVersion}
        // "Manage your plan or memberships": both routes are exempt above, so
        // the prompt steps aside there and returns on the next other route.
        onManageBilling={(route) => navigationRef.isReady?.() && navigationRef.navigate(route)}
        onAccepted={ackTosForSession}
        // Save failed on our side: the modal shows why, then steps aside for
        // the session (a failure must never gate the app). Next launch asks again.
        onDismissForSession={ackTosForSession}
        onReadFullTerms={() => {
          // Two things were wrong here and both mattered:
          //   1. 'Legal' is the plain-English explainer — it does NOT contain the
          //      numbered Terms, so it never showed Sections 10/11, the very
          //      clauses being accepted. The real text is the TosAgreement
          //      screen (/tos).
          //   2. This modal is a full-screen overlay, so navigating UNDER it
          //      just hides the Terms behind the dialog.
          // On web, open the Terms in their own tab: the prompt survives, and
          // the member can read and then come back and accept.
          // No 'noopener' feature string: with it window.open always returns
          // null, and a refused tab could not be told apart from an opened one.
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const w = window.open('/tos', '_blank');
            if (w) {
              try { w.opener = null; } catch (_) { /* cross-origin guard */ }
              return;
            }
            // Tab refused (popup blocker / webview): read in-app instead.
            // TosAgreement is in HIDE_TOS_PROMPT_ROUTES, so the prompt steps
            // aside there and returns on Back until they accept.
            if (navigationRef.isReady?.()) navigationRef.navigate('TosAgreement');
            return;
          }
          // Native has no second tab — drop the prompt so the Terms are
          // readable; it returns on the next launch until they accept.
          setNeedsTosAccept(false);
          if (navigationRef.isReady?.()) navigationRef.navigate('TosAgreement');
        }}
      />
      <FinishOnboardingModal
        visible={showOnboardingGuard && !!currentRouteName && !HIDE_ONBOARDING_PROMPT_ROUTES.includes(currentRouteName)}
        onComplete={() => {
          const u = auth.currentUser;
          const providerId = u?.providerData?.[0]?.providerId || '';
          if (navigationRef.isReady?.()) {
            navigationRef.navigate('SignUp', {
              oauthCompletion: true,
              provider: providerId === 'google.com' ? 'Google' : null,
              email: u?.email || null,
              displayName: u?.displayName || '',
            });
          }
        }}
      />
  </ErrorProvider>
  );
}

// =============================================================================
// LOADING SCREEN (shown while deep link is being parsed)
// =============================================================================
function LoadingScreen() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#0d0d0e',
      color: '#FFD700',
      fontFamily: 'Arial, sans-serif',
      fontSize: '18px',
    }}>
      Loading...
    </div>
  );
}

// =============================================================================
// AUTH LOADING SCREEN (shown while Firebase Auth initializes)
// =============================================================================
function AuthLoadingScreen() {
  return (
    <View style={{
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#0d0d0e',
    }}>
      <ActivityIndicator size="large" color="#FFD700" />
      <Text style={{ 
        color: '#FFD700', 
        marginTop: 16,
        fontSize: 16,
      }}>
        Loading...
      </Text>
    </View>
  );
}